import React, {useState, useEffect, useCallback, Suspense, lazy} from 'react';
import {Route, Switch, Redirect, useHistory} from 'react-router-dom';
import moment from 'moment';
import {useMediaQuery} from 'react-responsive';
import {Elements} from '@stripe/react-stripe-js';

import breakpoints from './components/helpers/breakpoints';
import {useTheme} from './context/ThemeProvider';
import {SocketProvider} from './context/SocketProvider';
import Home from './pages/Home';
import {Tools} from './pages/Tools';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import Articles from './pages/Articles';
import BlockList from './pages/BlockList';
import ResetPassword from './pages/ResetPassword';
import SharedGraph from './pages/SharedGraph';
import {AdsTXT} from './pages/AdsTXT';
import PublicAdsTxtRedirect from './pages/PublicAdsTxtRedirect';
import {Inventory} from './pages/Inventory';
import {CMP} from './pages/CMP';
import {MCM} from './pages/MCM';
import {AdManager} from './pages/AdManager';
import AdReports from './pages/AdReports';
import {GoogleAnalytics} from './pages/GoogleAnalytics';
import Feedback from './components/subcomponents/Feedback';
import YieldFormulaPromo from './components/subcomponents/YieldFormulaPromo';
import PolicyDialog from './components/subcomponents/PolicyDialog';
import ReviewUsPopup from './components/subcomponents/ReviewUsPopup';
import AccountsPropertiesSelection from './components/subcomponents/AccountsPropertiesSelection';
import Sidebar from './components/Sidebar';
import ProfileMenu from './components/ProfileMenu';
import ReactGA from 'react-ga4';
import lscache from 'lscache';
import {Helmet} from 'react-helmet';
import OnboardingWizzard from './components/subcomponents/OnboardingWizzard';
import DevPanel from './components/dev/DevPanel';
import {PopupsLogic} from './components/helpers/PopupsLogic';
import Alert from '@material-ui/lab/Alert';
import {APICall} from './api/api';
import getStripe from './components/misc/stripe.js';
import usePublishers from './hooks/usePublishers.js';
import useTicketsStatus from './hooks/useTicketsStatus.js';
import usePersistentAuthStore from './store/persistentAuthStore';
import useAuthInitialization from './hooks/useAuthInitialization';
import {isSelfServeClient} from './components/helpers/clientType';
import ClientChatbot from './components/chatbot/ClientChatbot';

import {
  Switch as UISwitch,
  AppBar,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  Tooltip,
  Container,
  useMediaQuery as useMediaQueryMui,
} from '@material-ui/core';

import {SnackbarProvider} from 'notistack';
import CustomSnackbar from './components/subcomponents/CustomSnackbar';

import {makeStyles} from '@material-ui/core/styles';
import {Link} from 'react-router-dom';

import {useSocket} from './context/SocketProvider';
import {useLocation} from 'react-router-dom';

// Icons
import {
  Menu as MenuIcon,
  WbSunny as WbSunnyIcon,
  NightsStay as NightsStayIcon,
} from '@material-ui/icons/';

import LogoDarkTheme from './components/img/setupad-logo-UA-white.svg';
import LogoWhiteTheme from './components/img/setupad-logo-UA-darkblue.svg';
import FavIconUA from './components/img/favIcon_UA.png';

// Global state
import useStore from './store/store';
import usePersistentStore from './store/persistentStore';

// Use refresh menu
import useRefreshMenu from './hooks/useRefreshMenu.js';

//import {BillingIndex} from './pages/BillingIndex';
const BillingIndex = lazy(() => import('./pages/BillingIndex'));

//import AdTableWithThemeMF from '@setupad/frontend-adserver';
//Import name is based on current branch name.
// For master branch, import adserver master, else dev
const AdTableWithThemeMF = lazy(
  () =>
    import(
      process.env.REACT_APP_CURRENT_BRANCH === 'master'
        ? 'frontend-adserver-master'
        : 'frontend-adserver-dev'
    )
);

//import Sales from './pages/Sales';
const Sales = lazy(() => import('./pages/Sales'));

//import { SuccessFormula } from './pages/SuccessFormula';
const SuccessFormula = lazy(() => import('./pages/SuccessFormula'));

//import Tickets from './pages/Tickets';
const Tickets = lazy(() => import('./pages/Tickets'));

//import Article from './pages/Article';
const Article = lazy(() => import('./pages/Article'));

const Subscription = lazy(() => import('./pages/Subscription'));

const DiscrepanciesPage = lazy(
  () => process.env.REACT_APP_CURRENT_BRANCH === 'master'
    ? import('./discrepancies/index.master')
    : import('./discrepancies/index.dev')
);

// Load Stripe
const stripePromise = getStripe();

const drawerWidth = 300;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexGrow: 1,
  },
  grow: {
    flexGrow: 1,
  },
  appBar: {
    marginLeft: drawerWidth,
    zIndex: theme.zIndex.drawer + 1,
    color: 'black',
    borderBottom: '1px solid #7979793b',
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  toolbar: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    padding: theme.spacing(0),
  },
  adserverContainer: {
    marginTop: 20,
    maxWidth: '95vw',
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
}));

// Dont load GA on self promo iframe
if (!window.location.href.includes('?utm_source=self-promo-client-ui')) {
  ReactGA.initialize('G-EPLE23BXTW');
}

function App({location}) {
  const {theme, toggleTheme} = useTheme();
  const isMobile = useMediaQuery({query: breakpoints.mobile});
  const isTablet = useMediaQuery({query: breakpoints.tablet});
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(
    lscache.get('drawerOpen') === null || lscache.get('drawerOpen') === true
  );

  useEffect(() => {
    lscache.set('drawerOpen', drawerOpen);
  }, [drawerOpen]);

  const [billingDetailsStatus, setBillingDetailsStatus] = useState(false);
  const [alertMessage, setAlertMessage] = React.useState(undefined);
  const setState = useStore(state => state.setState);
  const visiblePopup = useStore(state => state.visiblePopup);
  const locationHook = useLocation();

  const {refreshMenu} = useRefreshMenu();
  const {auth, updateAuthKey} = usePersistentAuthStore();
  const isSelfServe = isSelfServeClient(auth);
  const defaultTab = usePersistentStore(state => state.defaultTab);
  const setDefaultTab = usePersistentStore(state => state.setDefaultTab);
  const {forcedSelfServeOnboarding} = usePublishers();
  const history = useHistory();
  const {getTickets} = useTicketsStatus();
  const apiInspectorEnabled = usePersistentStore(state => state.apiInspectorEnabled) === true;

  const setPrebidPanelOnboardingFinished = useCallback(async () => {
    if (!auth?.token) return;
    const res = await APICall({
      endpoint: 'setAdserverOnboardingCompleted',
      options: {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.token}`,
          'Cache-Control': 'max-age=0, no-cache',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    });
    if (res?.success) updateAuthKey('adserver_onboarding_completed', 1);
  }, [auth?.token, updateAuthKey]);

  useEffect(() => {
    if (forcedSelfServeOnboarding) {
      history.push('/ad-manager');
    }
  }, [forcedSelfServeOnboarding, history]);

  // Websocket
  const socket = useSocket();

  // Initialize analytics and tracking services when auth changes
  useAuthInitialization(auth, setBillingDetailsStatus);

  useEffect(() => {
    if (auth?.token) {
      getNotificationMessage();
    }
  }, [auth]);

  // Apply overscroll-behavior-x: none to html and body only on /prebid-panel route
  useEffect(() => {
    const value = locationHook?.pathname.includes('prebid-panel') ? 'none' : '';
    document.documentElement.style.overscrollBehaviorX = value;
    document.body.style.overscrollBehaviorX = value;
  }, [locationHook?.pathname]);

  // Set default tab based on user's feature flags - runs once when auth/defaultTab changes
  useEffect(() => {
    if (!auth) return;

    // Check if current account has default section enabled in ClientUI settings
    if (
      (!auth.ssp_table_enabled && defaultTab === 2) ||
      (!auth.geo_data && defaultTab === 3) ||
      (!auth.sources_distribution_BETA && defaultTab === 4) ||
      (!auth.realtime && defaultTab === 0) ||
      (!auth.demand_split && defaultTab === 'stacked_demand') ||
      (!auth.prebid_stack && defaultTab === 'prebid_stack')
    ) {
      setDefaultTab(auth.realtime === 1 ? 0 : 1);
    } else {
      setState('selectedSection', defaultTab);
    }
  }, [auth, defaultTab, setDefaultTab, setState]);

  let getNotificationMessage = async () => {
    const response = await APICall({
      endpoint: 'getNotificationMessage',
      options: {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          'Cache-Control': 'max-age=0, no-cache',
          'Content-Type': 'application/json',
        },
      },
    });
    if (response) {
      setAlertMessage(response.message);
    }
  };

  const PrivateRoute = useCallback(
    ({component: Component, ...rest}) => {
      // TODO: double check if this works and if we need really use add auth to dependencies
      let routeProps = auth ? {...rest} : {};

      return (
        <Route
          {...routeProps}
          render={props =>
            auth ? (
              <Component {...rest} {...props} />
            ) : (
              <Redirect
                to={{
                  pathname: '/login',
                  state: {from: location},
                }}
              />
            )
          }
        />
      );
    },
    [location, auth]
  );

  const openDrawer = () => {
    ReactGA.event({
      category: 'Navigation Drawer toggle',
      action: 'click',
      label: drawerOpen ? 'close' : 'open',
    });
    setDrawerOpen(!drawerOpen);
  };

  return (
    <>
      <CssBaseline />

      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        autoHideDuration={5000}
        content={(key, messageObject) => <CustomSnackbar id={key} messageObject={messageObject} />}
      >
        <Helmet defer={false}>
          <link rel="shortcut icon" href={FavIconUA} />
          <meta name="theme-color" content={theme === 'light' ? '#FFF' : '#110f12'} />
        </Helmet>

        <div className={classes.root}>
          {auth?.menu && locationHook && !locationHook.pathname?.includes('wb/login') && (
            <>
              <OnboardingWizzard setDrawerOpen={setDrawerOpen} />
              <PopupsLogic />
              <ReviewUsPopup />
              <AppBar position="fixed" color="inherit" className={classes.appBar} elevation={0}>
                <PolicyDialog themeStyle={theme} />

                {/* <!-- Segment Pixel - Setupad_login - DO NOT MODIFY --> */}
                <img src="https://secure.adnxs.com/seg?add=10335915&t=2" width="1" height="1" />
                {/* <!-- End of Segment Pixel --> */}

                <Toolbar>
                  {!isMobile && (
                    <IconButton
                      edge="start"
                      className={classes.menuButton}
                      aria-label="open drawer"
                      onClick={openDrawer}
                    >
                      <MenuIcon />
                    </IconButton>
                  )}
                  <Tooltip arrow title={'Home'}>
                    <Typography className={classes.title} variant="h6" noWrap>
                      <Link to="/home">
                        <img
                          key={theme}
                          src={theme === 'dark' ? LogoDarkTheme : LogoWhiteTheme}
                          alt="Setupad logo"
                          style={{height: '42px', marginTop: '10px', marginLeft: '5px'}}
                        />
                      </Link>
                    </Typography>
                  </Tooltip>
                  {/* Render account property selector only for home route */}
                  <Switch>
                    {auth?.menu && !isTablet && !isMobile && auth?.menu?.menu?.[1] && (
                      <Route
                        path="/home/:publisher?/:domain?/:placement?"
                        render={() => (
                          <div
                            style={{
                              top: 11,
                              left: !drawerOpen ? 15 : 80,
                              position: 'relative',
                            }}
                          >
                            <AccountsPropertiesSelection />
                          </div>
                        )}
                      />
                    )}
                  </Switch>
                  <div className={classes.grow} />
                  {!isSelfServe && <Feedback />}

                  <Tooltip title={`${theme} theme`}>
                    <UISwitch
                      color={'primary'}
                      icon={
                        <WbSunnyIcon
                          style={{
                            color: 'white',
                            background: '#9e9e9e',
                            borderRadius: '50%',
                            border: '1px solid white',
                            top: -2,
                            position: 'relative',
                            padding: 2,
                          }}
                        />
                      }
                      checkedIcon={
                        <NightsStayIcon
                          style={{
                            color: 'white',
                            border: '1px solid #1c181d',
                            background: 'rgb(24 149 161)',
                            borderRadius: '50%',
                            top: -2,
                            position: 'relative',
                            padding: 2,
                          }}
                        />
                      }
                      checked={theme !== 'light'}
                      onChange={() => {
                        ReactGA.event({
                          category: 'Theme switch',
                          action: 'click',
                          label: theme === 'light' ? 'dark' : 'light',
                        });
                        toggleTheme();
                      }}
                    />
                  </Tooltip>
                  <ProfileMenu isMobile={isMobile} isSelfServe={isSelfServe} />
                </Toolbar>
              </AppBar>

              <Sidebar
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                isMobile={isMobile}
                isTablet={isTablet}
                auth={auth}
                visiblePopup={visiblePopup}
                billingDetailsStatus={billingDetailsStatus}
                locationHook={locationHook}
              />
            </>
          )}

          <main className={classes.content}>
            <div className={classes.toolbar} />

            {auth?.menu && alertMessage && (
              <Alert severity={alertMessage.severity}>{alertMessage.text}</Alert>
            )}

            {auth?.menu &&
              auth?.yield_formula_enabled === 1 &&
              moment().diff(auth?.creationDate, 'days') > 30 &&
              !isMobile &&
              lscache.get('mobile_alert_closed') &&
              !lscache.get('yield_promo_closed') && <YieldFormulaPromo />}

            <Switch>
              <Route
                path="/forgot-password"
                exact
                render={props => <ForgotPassword {...props} />}
              />

              <Route
                path="/change-password"
                exact
                render={props => <ChangePassword {...props} isSelfServe={isSelfServe} />}
              />

              <Route
                path="/shared/:identifier"
                exact
                render={props => <SharedGraph {...props} />}
              />

              <Route
                path="/reset-password/:username/:userId/:token"
                exact
                render={props => <ResetPassword {...props} />}
              />

              <Route
                path="/ads-txt-redirect/:accountId/:mode/:domainId/:domainName"
                exact
                render={props => <PublicAdsTxtRedirect {...props} />}
              />

              <Route
                path="/article/:articleId/:title"
                exact
                render={props => (
                  <Suspense fallback={<div>Loading...</div>}>
                    <Article {...props} />
                  </Suspense>
                )}
              />

              <Route path="/" exact render={props => <Login {...props} theme={theme} />} />
              <Route
                path="/login"
                exact
                render={props => <Login {...props} auth={auth} theme={theme} />}
              />

              <PrivateRoute path="/change-password" exact>
                <ChangePassword login={auth} isSelfServe={isSelfServe} />
              </PrivateRoute>

              <PrivateRoute path="/home/:publisher?/:domain?/:placement?" exact>
                <Home
                  login={auth}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  isSelfServe={isSelfServe}
                  setDrawerOpen={setDrawerOpen}
                />
              </PrivateRoute>

              <PrivateRoute path="/ads-txt" exact>
                <AdsTXT login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/inventory" exact>
                <Inventory login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/google-analytics" exact>
                <GoogleAnalytics login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/yield-formula" exact>
                <Suspense fallback={<div>Loading...</div>}>
                  <SuccessFormula login={auth} />
                </Suspense>
              </PrivateRoute>

              <PrivateRoute path="/tools" exact>
                <Tools login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/knowledge-base" exact>
                <Articles login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/block-list" exact>
                <BlockList getTickets={getTickets} />
              </PrivateRoute>

              <PrivateRoute path="/support" exact>
                <Suspense fallback={<div>Loading...</div>}>
                  <Tickets />
                </Suspense>
              </PrivateRoute>

              <PrivateRoute path="/blocked-ads" exact>
                <AdReports login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/cmp" exact>
                <CMP login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/mcm" exact>
                <MCM login={auth} />
              </PrivateRoute>

              <PrivateRoute path="/ad-manager" exact>
                <AdManager login={auth} />
              </PrivateRoute>

              {auth?.discrepancies_enabled === 1 && (
                <PrivateRoute path="/discrepancies" exact component>
                  <Suspense fallback={<div>Loading...</div>}>
                    <DiscrepanciesPage token={auth?.token} auth={auth} />
                  </Suspense>
                </PrivateRoute>
              )}

              <PrivateRoute path="/sales" exact component>
                <Suspense fallback={<div>Loading...</div>}>
                  <Sales login={auth} />
                </Suspense>
              </PrivateRoute>

              <PrivateRoute path="/prebid-panel" exact component>
                <Container
                  component="main"
                  className={classes.adserverContainer}
                  style={{
                    width: drawerOpen ? `calc(95vw - ${drawerWidth}px)` : '95vw',
                  }}
                >
                  <Suspense fallback={<div>Loading...</div>}>
                    <AdTableWithThemeMF
                      socket={socket}
                      clientUIToken={auth?.token}
                      clientUIReportingToken={auth?.reportingToken}
                      theme={theme}
                      refreshMenu={refreshMenu}
                      getTickets={getTickets}
                      setPrebidPanelOnboardingFinished={setPrebidPanelOnboardingFinished}
                      onboardingEnabled={
                        !auth?.adserver_onboarding_completed && auth?.onboarding_enabled === 1
                      }
                      wbLogin={auth?.wb_auth}
                      savePpColumnsInLocalStorage={auth?.save_pp_columns_in_local_storage === 1}
                      REACT_APP_CURRENT_BRANCH={process.env.REACT_APP_CURRENT_BRANCH}
                    />
                  </Suspense>
                </Container>
              </PrivateRoute>

              <PrivateRoute path="/subscription" exact component>
                <Elements stripe={stripePromise}>
                  <Suspense fallback={<div>Loading...</div>}>
                    <Subscription login={auth} />
                  </Suspense>
                </Elements>
              </PrivateRoute>

              {auth?.billingEnabled === 1 && (
                <>
                  <PrivateRoute path={'/billing'} exact component>
                    <Suspense fallback={<div>Loading...</div>}>
                      {auth?.type === 7 || auth?.type === 4 ? (
                        <Elements stripe={stripePromise}>
                          <Subscription />
                        </Elements>
                      ) : (
                        <BillingIndex />
                      )}
                    </Suspense>
                  </PrivateRoute>
                </>
              )}

              {/*// Default route to catch all requests*/}
              <Route path="*" exact render={props => <Login {...props} />} />
            </Switch>
          </main>
        </div>
        {auth?.token && auth?.wb_auth && <ClientChatbot />}
        {auth?.token && apiInspectorEnabled && <DevPanel />}
      </SnackbarProvider>
    </>
  );
}

// Wrap App with SocketProvider so it doesn't affect LoginWB route
const AppWithSocket = props => (
  <SocketProvider>
    <App {...props} />
  </SocketProvider>
);

export default AppWithSocket;
